import 'utils/startup-splash';
import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import * as Sentry from '@sentry/react-native';
import ErrorBoundary from 'components/atoms/ErrorBoundary';
import LoadingComponent from 'components/atoms/Loading';
import WebContainerView from 'components/atoms/WebContainerView';
import ConfigProvider, { configClient } from 'components/provider/ConfigProvider';
import ServerProvider from 'components/provider/ServerProvider';
import SplashProvider from 'components/provider/SplashProvider';
import 'expo-asset'; // For expo-updates
import * as Font from 'expo-font';
import * as Updates from 'expo-updates';
import 'intl-pluralrules';
import 'polyfills/base64';
import 'polyfills/intlPolyfills';
import { Suspense, useEffect, useState } from 'react';
import 'react-native-gesture-handler';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import 'react-native-reanimated';
import { initialWindowMetrics, SafeAreaProvider } from 'react-native-safe-area-context';
import { enableFreeze, enableScreens } from 'react-native-screens';
import { Provider } from 'react-redux';
import IndexScene from "scenes";
import ResponseIndex from 'scenes/response';
import store from "services/redux/store";
import { storeKeys } from 'services/store/keys';
import SecureStoreService from 'services/store/secure-store';
import { layoutStyles } from 'styles/layout';
import CacheStorage from 'utils/cache/CacheStorage';
import 'utils/console-prefix';
import { useIsSmallDevice } from 'utils/helper-device';
import { useIsInEmailResponse, useIsInManagement } from 'utils/helper-linking';
import HelperPlatform from "utils/helper-platform";
import HelperVersion from 'utils/helper-version';

enableScreens();
enableFreeze( true );

Sentry.init( {
	dsn: 'https://422ca54f05a744c3ac4ea1cce892cb6d@o349306.ingest.sentry.io/5396856',
	enableInExpoDevelopment: false,
	debug: false, // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production
	enableNative: true,
	enabled: !__DEV__,
	release: HelperVersion.getBuildVersion(),
});

function cacheFonts( fonts ) {
	return fonts.map( font => {
		return Font.loadAsync( font )
	} );
}

const App = () => {
	const [ loaded, setLoaded ] = useState( false )
	const [ startupReady, setStartupReady ] = useState( false )
	
	const loadAssetsAsync = async () => {
		const fontAssets = cacheFonts([
			{
				// 'Montserrat-Regular': require('./assets/fonts/Montserrat-Regular.ttf'),
				// 'Montserrat-Bold': require('./assets/fonts/Montserrat-Bold.ttf'),
				'OpenSans-Light': require('./assets/fonts/OpenSans-Light.ttf'),
				'OpenSans-Regular': require('./assets/fonts/OpenSans-Regular.ttf'),
				'OpenSans-SemiBold': require('./assets/fonts/OpenSans-SemiBold.ttf'),
				'OpenSans-Bold': require('./assets/fonts/OpenSans-Bold.ttf'),
			},
			MaterialCommunityIcons.font,
			FontAwesome5.font
		]);
		
		await Promise.all([
			...fontAssets
		]);
	}
	
	const checkVersion = async () => {
		// Check if the app is updated
		const version = await SecureStoreService.get( storeKeys.APP_UPDATE_ID );
		const newUpdateId = Updates.updateId;

		// In dev version is always null
		if( version !== null && newUpdateId !== null && version !== newUpdateId ) {
			await CacheStorage.clearCache( { restart: false } );
			await SecureStoreService.set( storeKeys.APP_UPDATE_ID, newUpdateId );
		}
	}
	
	useEffect(() => {
		checkVersion()
			.catch( error => {
				Sentry.captureException( error );
			})
			.finally( () => {
				setStartupReady( true );
			});
		
		loadAssetsAsync()
			.catch( error => {
				Sentry.captureException( error );
			})
			.finally( () => {
				setLoaded( true )
			});
	}, [] )
	
	return (
		<Suspense
			fallback={ <LoadingComponent /> }>
			
			<GestureHandlerRootView
				style={ layoutStyles.flexOne }>
				
				<Provider
					store={ store }>
						
					<SafeAreaProvider
						initialMetrics={ initialWindowMetrics }>
						
						<ErrorBoundary>
							<AppContent
								loaded={ loaded }
								startupReady={ startupReady }
							/>
						</ErrorBoundary>
					</SafeAreaProvider>
				</Provider>
			</GestureHandlerRootView>
		</Suspense>
	);
}

const AppContent = ({ loaded = false, startupReady = false }) => {
	
	const isInManagement = useIsInManagement()
	const isInEmailResponse = useIsInEmailResponse()
	const isSmallDevice = useIsSmallDevice();
	
	if( isInEmailResponse ) {
		return (
			<SplashProvider>

				{ startupReady && (
					<ConfigProvider
						config={ configClient }>
						
						<ServerProvider>

							{ loaded && <ResponseIndex /> }
						</ServerProvider>
					</ConfigProvider>
				) }
			</SplashProvider>
		);
	}

	// Keep the same splash mounted while fonts and session setup run in parallel.
	let content = (
		<IndexScene
			assetsLoaded={ loaded }
			startupReady={ startupReady }
		/>
	);

	if( !isInManagement && !isSmallDevice && HelperPlatform.isWeb() ) {
		content = (
			<WebContainerView>

				{ content }
			</WebContainerView>
		);
	}
	
	return content;
}

export default Sentry.wrap( App )
